/**
 * Food Specialties Section Component
 * Displays local food specialties in a grid layout
 * Follows Single Responsibility Principle - only responsible for rendering the section with items
 */

import React from 'react';
import { spacing } from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { FoodSpecialtyItem, FoodSpecialty } from './FoodSpecialtyItem';

export interface FoodSpecialtiesSectionProps {
  /**
   * Section title
   * @default 'Local food specialities'
   */
  title?: string;
  /**
   * Array of food specialty items to display
   */
  items: FoodSpecialty[];
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Place name for image fetching context
   */
  placeName?: string;
}

/**
 * Food Specialties Section Component
 * Renders a grid of food specialty items with responsive layout
 */
export const FoodSpecialtiesSection: React.FC<FoodSpecialtiesSectionProps> = ({
  title = 'Local food specialities',
  items,
  className,
  placeName,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
      className={className}
    >
      <SectionHeader title={title} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: spacing[3],
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
        }}
      >
        {items.map(food => (
          <FoodSpecialtyItem key={food.id} food={food} placeName={placeName} />
        ))}
      </div>
    </div>
  );
};
